import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import UnitDealDetailsSlider from '../components/UnitDealDetailsSlider';
import { Search, Building } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';
import Pagination from 'react-responsive-pagination';
import 'react-responsive-pagination/themes/classic.css';
import '../styles/Builders.css';
import '../styles/Buttons.css';
import '../styles/DataTable.css';
import '../styles/UnitDeal.css';

interface Unit {
  _id: string;
  project: string;
  modelCollection: string;
  modelName: string;
  type: string;
  unitNumber: string;
  unitStatus: string;
  deal: string;
  dealStatus: string;
  designStudioStatus: string;
  colorCollection: string;
  packages: string;
  options: string;
  accessToPP: boolean;
  designStudio: boolean;
  colorSelection: boolean;
  packageSelection: boolean;
  parkingSelection: boolean;
  bicycleSelection: boolean;
  lockerSelection: boolean;
  layoutCustomization: boolean;
}

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface Project {
  _id: string;
  name: string;
}

const UnitDeal: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [searchInput, setSearchInput] = useState(''); // User's input
  const [searchQuery, setSearchQuery] = useState(''); // Debounced value for API
  const [units, setUnits] = useState<Unit[]>([]);
  const isFetchingRef = useRef(false);
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Unit details slider - initialize from URL parameter
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(
    searchParams.get('unitId') || null
  );

  // Filters
  const [selectedProject, setSelectedProject] = useState('all');
  const [selectedFloor, setSelectedFloor] = useState('all');
  const [selectedBeds, setSelectedBeds] = useState('all');
  const [selectedBaths, setSelectedBaths] = useState('all');
  const [selectedMedia, setSelectedMedia] = useState('all');
  const [selectedFlex, setSelectedFlex] = useState('all');
  const [selectedCeiling, setSelectedCeiling] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedDesignStudio, setSelectedDesignStudio] = useState('all');
  const [selectedParkingStatus, setSelectedParkingStatus] = useState('all');
  const [selectedBicycleStatus, setSelectedBicycleStatus] = useState('all');
  const [selectedLockerStatus, setSelectedLockerStatus] = useState('all');

  // Filter options
  const [filterOptions, setFilterOptions] = useState<any>({
    projects: [] as Project[],
    floors: [],
    beds: [],
    baths: [],
    media: [],
    flex: [],
    ceiling: [],
    statuses: [],
    designStudioStatuses: [],
    parkingStatuses: [],
    bicycleStatuses: [],
    lockerStatuses: []
  });

  // Pagination
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    if (workspaceId) {
      loadFilterOptions();
      loadUnits(false);
    }
  }, [workspaceId]);

  // Debounce search input - wait 500ms after user stops typing
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 500);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchInput]);

  // Reset to page 1 when filters or search change
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [
    selectedProject,
    selectedFloor,
    selectedBeds,
    selectedBaths,
    selectedMedia,
    selectedFlex,
    selectedCeiling,
    selectedStatus,
    selectedDesignStudio,
    selectedParkingStatus,
    selectedBicycleStatus,
    selectedLockerStatus,
    searchQuery
  ]);

  useEffect(() => {
    if (workspaceId && !loading && !isFetchingRef.current) {
      loadUnits(true);
    }
  }, [
    pagination.page,
    pagination.limit,
    selectedProject,
    selectedFloor,
    selectedBeds,
    selectedBaths,
    selectedMedia,
    selectedFlex,
    selectedCeiling,
    selectedStatus,
    selectedDesignStudio,
    selectedParkingStatus,
    selectedBicycleStatus,
    selectedLockerStatus,
    searchQuery
  ]);

  const loadFilterOptions = async () => {
    try {
      const response = await api.get(`/unit-deals/${workspaceId}/filter-options`);
      setFilterOptions(response.data);
    } catch (error: any) {
      console.error('Error loading filter options:', error);
      toast.error(error.response?.data?.message || 'Failed to load filter options');
    }
  };

  const loadUnits = async (isDataRefresh: boolean = false) => {
    // Prevent duplicate calls
    if (isFetchingRef.current) {
      return;
    }

    try {
      isFetchingRef.current = true;

      // Use dataLoading for data refreshes (pagination, filters), loading for initial load
      if (isDataRefresh) {
        setDataLoading(true);
      } else {
        setLoading(true);
      }

      const params: any = {
        page: pagination.page,
        limit: pagination.limit
      };

      if (selectedProject !== 'all') params.project = selectedProject;
      if (selectedFloor !== 'all') params.floor = selectedFloor;
      if (selectedBeds !== 'all') params.beds = selectedBeds;
      if (selectedBaths !== 'all') params.baths = selectedBaths;
      if (selectedMedia !== 'all') params.media = selectedMedia;
      if (selectedFlex !== 'all') params.flex = selectedFlex;
      if (selectedCeiling !== 'all') params.ceiling = selectedCeiling;
      if (selectedStatus !== 'all') params.status = selectedStatus;
      if (selectedDesignStudio !== 'all') params.designStudio = selectedDesignStudio;
      if (selectedParkingStatus !== 'all') params.parkingStatus = selectedParkingStatus;
      if (selectedBicycleStatus !== 'all') params.bicycleStatus = selectedBicycleStatus;
      if (selectedLockerStatus !== 'all') params.lockerStatus = selectedLockerStatus;
      if (searchQuery) params.search = searchQuery;

      const response = await api.get(`/unit-deals/${workspaceId}`, { params });
      setUnits(response.data.units);

      // Update pagination if values changed
      const newPagination = response.data.pagination;
      if (
        newPagination.page !== pagination.page ||
        newPagination.limit !== pagination.limit ||
        newPagination.total !== pagination.total ||
        newPagination.pages !== pagination.pages
      ) {
        setPagination(newPagination);
      }
    } catch (error: any) {
      console.error('Error loading units:', error);
      toast.error(error.response?.data?.message || 'Failed to load units');
    } finally {
      if (isDataRefresh) {
        setDataLoading(false);
      } else {
        setLoading(false);
      }
      isFetchingRef.current = false;
    }
  };

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  const handleClearFilters = () => {
    setSelectedProject('all');
    setSelectedFloor('all');
    setSelectedBeds('all');
    setSelectedBaths('all');
    setSelectedMedia('all');
    setSelectedFlex('all');
    setSelectedCeiling('all');
    setSelectedStatus('all');
    setSelectedDesignStudio('all');
    setSelectedParkingStatus('all');
    setSelectedBicycleStatus('all');
    setSelectedLockerStatus('all');
    setSearchInput('');
    setSearchQuery('');
  };

  const toggleAccess = async (unitId: string, field: string, currentValue: boolean) => {
    try {
      await api.patch(`/unit-deals/${workspaceId}/unit/${unitId}/toggle-access`, {
        field,
        value: !currentValue
      });

      // Update local state
      setUnits(prevUnits =>
        prevUnits.map(unit =>
          unit._id === unitId
            ? { ...unit, [field]: !currentValue }
            : unit
        )
      );

      toast.success('Portal access updated successfully');
    } catch (error: any) {
      console.error('Error toggling portal access:', error);
      toast.error(error.response?.data?.message || 'Failed to update portal access');
    }
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handleUnitClick = (unitId: string) => {
    setSelectedUnitId(unitId);
    setSearchParams({ unitId });
  };

  const handleCloseSlider = () => {
    setSelectedUnitId(null);
    setSearchParams({});
  };

  const handleLimitChange = (limit: number) => {
    setPagination(prev => ({ ...prev, limit, page: 1 }));
  };

  return (
    <AppLayout onBack={handleBackToWorkspaces}>
      <div className="page-container">
        <div className="page-header">
          <div>
            <h1 style={{ margin: 0 }}>Unit/Deal Management</h1>
            <p style={{ fontSize: '14px', color: '#6B7280', margin: '8px 0 0 0' }}>
              Manage unit allocations and purchaser portal access
            </p>
          </div>
        </div>

        <div className="page-content" style={{ marginTop: '24px' }}>
          {loading ? (
            <div className="builders-loading">
              <div className="builders-loading-spinner" />
            </div>
          ) : (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
            }}>
              {/* Filters */}
              <div style={{ marginBottom: '20px' }}>
                {/* First Row of Filters */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '12px',
                  marginBottom: '12px'
                }}>
                  <select
                    className="form-input"
                    value={selectedProject}
                    onChange={(e) => setSelectedProject(e.target.value)}
                    style={{ cursor: 'pointer' }}
                  >
                    <option value="all">All Project</option>
                    {filterOptions.projects.map((project: Project) => (
                      <option key={project._id} value={project._id}>{project.name}</option>
                    ))}
                  </select>

                  <select
                    className="form-input"
                    value={selectedFloor}
                    onChange={(e) => setSelectedFloor(e.target.value)}
                    style={{ cursor: 'pointer' }}
                  >
                    <option value="all">Select Floor</option>
                    {filterOptions.floors.map((floor: number) => (
                      <option key={floor} value={floor}>Floor {floor}</option>
                    ))}
                  </select>

                  <select
                    className="form-input"
                    value={selectedBeds}
                    onChange={(e) => setSelectedBeds(e.target.value)}
                    style={{ cursor: 'pointer' }}
                  >
                    <option value="all">Select Beds</option>
                    {filterOptions.beds.map((bed: number) => (
                      <option key={bed} value={bed}>{bed} Bed{bed !== 1 ? 's' : ''}</option>
                    ))}
                  </select>

                  <select
                    className="form-input"
                    value={selectedBaths}
                    onChange={(e) => setSelectedBaths(e.target.value)}
                    style={{ cursor: 'pointer' }}
                  >
                    <option value="all">Select Baths</option>
                    {filterOptions.baths.map((bath: number) => (
                      <option key={bath} value={bath}>{bath} Bath{bath !== 1 ? 's' : ''}</option>
                    ))}
                  </select>
                </div>

                {/* Second Row of Filters */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '12px',
                  marginBottom: '12px'
                }}>
                  <select
                    className="form-input"
                    value={selectedMedia}
                    onChange={(e) => setSelectedMedia(e.target.value)}
                    style={{ cursor: 'pointer' }}
                  >
                    <option value="all">Select Media</option>
                    {filterOptions.media.map((media: number) => (
                      <option key={media} value={media}>{media} Media</option>
                    ))}
                  </select>

                  <select
                    className="form-input"
                    value={selectedFlex}
                    onChange={(e) => setSelectedFlex(e.target.value)}
                    style={{ cursor: 'pointer' }}
                  >
                    <option value="all">Select Flex</option>
                    {filterOptions.flex.map((flex: number) => (
                      <option key={flex} value={flex}>{flex} Flex</option>
                    ))}
                  </select>

                  <select
                    className="form-input"
                    value={selectedCeiling}
                    onChange={(e) => setSelectedCeiling(e.target.value)}
                    style={{ cursor: 'pointer' }}
                  >
                    <option value="all">Select Ceiling</option>
                    {filterOptions.ceiling.map((ceiling: number) => (
                      <option key={ceiling} value={ceiling}>{ceiling} ft</option>
                    ))}
                  </select>

                  <select
                    className="form-input"
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    style={{ cursor: 'pointer' }}
                  >
                    <option value="all">Select Status</option>
                    {filterOptions.statuses.map((status: string) => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>

                {/* Third Row of Filters */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '12px',
                  marginBottom: '12px'
                }}>
                  <select
                    className="form-input"
                    value={selectedDesignStudio}
                    onChange={(e) => setSelectedDesignStudio(e.target.value)}
                    style={{ cursor: 'pointer' }}
                  >
                    <option value="all">Select Design Studio</option>
                    {filterOptions.designStudioStatuses.map((status: string) => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>

                  <select
                    className="form-input"
                    value={selectedParkingStatus}
                    onChange={(e) => setSelectedParkingStatus(e.target.value)}
                    style={{ cursor: 'pointer' }}
                  >
                    <option value="all">Select Parking Status</option>
                    <option value="selected">Selected</option>
                    <option value="not-selected">Not Selected</option>
                  </select>

                  <select
                    className="form-input"
                    value={selectedBicycleStatus}
                    onChange={(e) => setSelectedBicycleStatus(e.target.value)}
                    style={{ cursor: 'pointer' }}
                  >
                    <option value="all">Select Bicycle Status</option>
                    <option value="selected">Selected</option>
                    <option value="not-selected">Not Selected</option>
                  </select>

                  <select
                    className="form-input"
                    value={selectedLockerStatus}
                    onChange={(e) => setSelectedLockerStatus(e.target.value)}
                    style={{ cursor: 'pointer' }}
                  >
                    <option value="all">Select Locker Status</option>
                    <option value="selected">Selected</option>
                    <option value="not-selected">Not Selected</option>
                  </select>
                </div>

                {/* Search and Clear */}
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'center'
                }}>
                  <div style={{ position: 'relative', maxWidth: '300px', flex: '0 1 300px' }}>
                    <Search
                      size={18}
                      style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#9CA3AF'
                      }}
                    />
                    <input
                      type="text"
                      placeholder="Search"
                      value={searchInput}
                      onChange={handleSearchChange}
                      className="form-input"
                      style={{
                        width: '100%',
                        paddingLeft: '40px'
                      }}
                    />
                  </div>
                  <button
                    className="btn btn-secondary btn-md"
                    onClick={handleClearFilters}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Clear
                  </button>
                </div>
              </div>

              {/* Units Table */}
              {units.length === 0 ? (
                <div className="data-table-container">
                  <div style={{
                    textAlign: 'center',
                    padding: '60px 20px',
                    color: '#6B7280'
                  }}>
                    <Building size={48} color="#D1D5DB" style={{ marginBottom: '16px' }} />
                    <p style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                      No units found
                    </p>
                    <p style={{ fontSize: '14px' }}>
                      Units with deals will appear here
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="data-table-container" style={{ position: 'relative', overflowX: 'auto' }}>
                    {/* Loading Overlay */}
                    {dataLoading && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10
                      }}>
                        <div className="builders-loading-spinner"></div>
                      </div>
                    )}

                    <table className="data-table" style={{ opacity: dataLoading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                    <thead>
                      <tr>
                        <th>Project</th>
                        <th>Model Collection</th>
                        <th style={{ minWidth: '120px' }}>Model Name</th>
                        <th style={{ minWidth: '100px' }}>Type</th>
                        <th>Unit#</th>
                        <th>Unit Status</th>
                        <th style={{ minWidth: '220px' }}>Deal</th>
                        <th style={{ minWidth: '130px' }}>Deal Status</th>
                        <th>Design Studio Status</th>
                        <th>Color Collection</th>
                        <th>Packages</th>
                        <th>Options</th>
                        <th>Access to PP</th>
                        <th>Design Studio</th>
                        <th>Color Selection</th>
                        <th>Package Selection</th>
                        <th>Parking Selection</th>
                        <th>Bicycle Selection</th>
                        <th>Locker Selection</th>
                        <th>Layout Customization</th>
                      </tr>
                    </thead>
                    <tbody>
                      {units.map((unit) => (
                        <tr key={unit._id}>
                          <td className="data-table-primary-cell">{unit.project}</td>
                          <td>{unit.modelCollection}</td>
                          <td>{unit.modelName}</td>
                          <td>{unit.type}</td>
                          <td className="data-table-secondary-cell">
                            <button
                              onClick={() => handleUnitClick(unit._id)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#48182F',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                padding: 0,
                                font: 'inherit'
                              }}
                            >
                              {unit.unitNumber}
                            </button>
                          </td>
                          <td>
                            <span style={{
                              display: 'inline-block',
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '500',
                              backgroundColor: '#10B98115',
                              color: '#10B981'
                            }}>
                              {unit.unitStatus}
                            </span>
                          </td>
                          <td>{unit.deal}</td>
                          <td>
                            <span style={{
                              display: 'inline-block',
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '500',
                              backgroundColor: '#F59E0B15',
                              color: '#F59E0B'
                            }}>
                              {unit.dealStatus}
                            </span>
                          </td>
                          <td>{unit.designStudioStatus}</td>
                          <td>{unit.colorCollection}</td>
                          <td>{unit.packages}</td>
                          <td>{unit.options}</td>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={unit.accessToPP}
                              onChange={() => toggleAccess(unit._id, 'accessToPP', unit.accessToPP)}
                            />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={unit.designStudio}
                              onChange={() => toggleAccess(unit._id, 'designStudio', unit.designStudio)}
                            />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={unit.colorSelection}
                              onChange={() => toggleAccess(unit._id, 'colorSelection', unit.colorSelection)}
                            />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={unit.packageSelection}
                              onChange={() => toggleAccess(unit._id, 'packageSelection', unit.packageSelection)}
                            />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={unit.parkingSelection}
                              onChange={() => toggleAccess(unit._id, 'parkingSelection', unit.parkingSelection)}
                            />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={unit.bicycleSelection}
                              onChange={() => toggleAccess(unit._id, 'bicycleSelection', unit.bicycleSelection)}
                            />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={unit.lockerSelection}
                              onChange={() => toggleAccess(unit._id, 'lockerSelection', unit.lockerSelection)}
                            />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={unit.layoutCustomization}
                              onChange={() => toggleAccess(unit._id, 'layoutCustomization', unit.layoutCustomization)}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '20px',
                  gap: '16px',
                  flexWrap: 'wrap'
                }}>
                  {/* Records per page selector */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    color: '#6B7280'
                  }}>
                    <span>Show</span>
                    <select
                      className="form-input"
                      style={{
                        padding: '6px 12px',
                        fontSize: '14px',
                        width: 'auto',
                        minWidth: '70px'
                      }}
                      value={pagination.limit}
                      onChange={(e) => handleLimitChange(Number(e.target.value))}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span>records per page (Total: {pagination.total})</span>
                  </div>

                  {/* Pagination */}
                  {pagination.pages > 1 && (
                    <div className="unit-deal-pagination-wrapper">
                      <Pagination
                        current={pagination.page}
                        total={pagination.pages}
                        onPageChange={handlePageChange}
                        maxWidth={400}
                      />
                    </div>
                  )}
                </div>
              </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Unit Deal Details Slider */}
      {selectedUnitId && workspaceId && (
        <UnitDealDetailsSlider
          workspaceId={workspaceId}
          unitId={selectedUnitId}
          onClose={handleCloseSlider}
          onSuccess={() => {
            // Reload units list but don't close the slider
            loadUnits(true);
          }}
        />
      )}
    </AppLayout>
  );
};

export default UnitDeal;
